/**
 * Append-only ledger of every tool invocation, with optional undo closures.
 * The UI panel subscribes to it; tools never touch it directly.
 */

export type LedgerSource = 'agent' | 'inspector' | 'human';

export type UndoFn = () => void | Promise<void>;

export interface LedgerEntry {
  id: string;
  tool: string;
  input: unknown;
  /** One-line human summary of what happened. */
  summary: string;
  result?: unknown;
  error?: { code: string; message: string; issues?: string[] };
  at: number;
  durationMs?: number;
  source: LedgerSource;
  undo?: UndoFn;
  undoLabel?: string;
  undone?: boolean;
}

type Listener = (entries: readonly LedgerEntry[]) => void;

const entries: LedgerEntry[] = [];
const listeners = new Set<Listener>();
let seq = 0;

function emit() {
  const snapshot = entries.slice();
  for (const l of listeners) l(snapshot);
}

export const ledger = {
  /** Newest first. */
  entries(): readonly LedgerEntry[] {
    return entries.slice();
  },

  append(e: Omit<LedgerEntry, 'id' | 'at'> & { at?: number }): LedgerEntry {
    const entry: LedgerEntry = { id: `l${++seq}`, at: e.at ?? Date.now(), ...e };
    entries.unshift(entry);
    if (entries.length > 200) entries.length = 200;
    emit();
    return entry;
  },

  async undo(id: string): Promise<boolean> {
    const e = entries.find((x) => x.id === id);
    if (!e || !e.undo || e.undone) return false;
    await e.undo();
    e.undone = true;
    emit();
    return true;
  },

  clear() {
    entries.length = 0;
    emit();
  },

  subscribe(l: Listener): () => void {
    listeners.add(l);
    l(entries.slice());
    return () => listeners.delete(l);
  },
};
